import { useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { CustomButton } from "src/design/CustomButton";
import { CustomLoadingButton } from "src/design/CustomLoadingButton";
import { SnackBar } from "src/design/SnackBar";
import { InfoCard } from "src/design/InfoCard";
import {
  colorTextPrimary,
  colorTextMuted,
  colorBorder,
  colorActionDanger,
  colorSurfaceDangerHover,
  colorTextError,
} from "src/design/tokens";
import {
  useStaffPermissions,
  useCreateStaffPermission,
  useDeleteStaffPermission,
} from "src/data/admin/queries/staffPermission";
import { StaffPermissionDto } from "src/data/admin/types/staffPermission";

const Permissions = () => {
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createDescription, setCreateDescription] = useState("");
  const [pendingDelete, setPendingDelete] = useState<StaffPermissionDto | null>(null);
  const [snack, setSnack] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const { data: permissions, isLoading } = useStaffPermissions();

  const createMutation = useCreateStaffPermission((p) => {
    setCreateOpen(false);
    setCreateName("");
    setCreateDescription("");
    setSnack({ open: true, message: `Permission "${p.name}" created.`, severity: "success" });
  });

  const deleteMutation = useDeleteStaffPermission(() => {
    setSnack({
      open: true,
      message: `Permission "${pendingDelete?.name}" deleted.`,
      severity: "success",
    });
    setPendingDelete(null);
  });

  const filtered = (permissions ?? []).filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleCreate = () => {
    if (!createName.trim()) return;
    createMutation.mutate(
      { name: createName.trim(), description: createDescription.trim() || undefined },
      {
        onError: (err) =>
          setSnack({
            open: true,
            message: err instanceof Error ? err.message : "An error occurred",
            severity: "error",
          }),
      }
    );
  };

  const handleDelete = () => {
    if (!pendingDelete) return;
    deleteMutation.mutate(pendingDelete.id, {
      onError: (err) =>
        setSnack({
          open: true,
          message: err instanceof Error ? err.message : "An error occurred",
          severity: "error",
        }),
    });
  };

  const handleCloseCreate = () => {
    if (createMutation.isPending) return;
    setCreateOpen(false);
    setCreateName("");
    setCreateDescription("");
  };

  return (
    <>
      <Box>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: colorTextPrimary }}>
            Staff Permissions
          </Typography>
          <CustomButton
            color="black"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
          >
            New Permission
          </CustomButton>
        </Box>

        {/* Search */}
        <TextField
          size="small"
          placeholder="Search permissions…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: colorTextMuted, fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 3, width: { xs: "100%", md: 320 } }}
        />

        {/* Table */}
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <InfoCard
            title={`Permissions (${filtered.length})`}
            icon={<LockOutlinedIcon fontSize="small" />}
          >
            {filtered.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <Typography variant="body2" sx={{ color: colorTextMuted }}>
                  {search ? "No permissions match your search." : "No permissions yet."}
                </Typography>
              </Box>
            ) : (
              <Table size="small" sx={{ mt: -1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: colorTextMuted, fontWeight: 600, pl: 0 }}>
                      Name
                    </TableCell>
                    <TableCell sx={{ color: colorTextMuted, fontWeight: 600 }}>
                      Description
                    </TableCell>
                    <TableCell sx={{ color: colorTextMuted, fontWeight: 600 }}>
                      ID
                    </TableCell>
                    <TableCell align="right" sx={{ pr: 0 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((permission) => (
                    <TableRow
                      key={permission.id}
                      sx={{
                        "&:last-child td": { borderBottom: 0 },
                        "&:hover": { backgroundColor: "action.hover" },
                      }}
                    >
                      <TableCell sx={{ pl: 0, fontWeight: 500, color: colorTextPrimary }}>
                        {permission.name}
                      </TableCell>
                      <TableCell sx={{ color: colorTextMuted }}>
                        {permission.description ?? "—"}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: colorTextMuted,
                          fontFamily: "monospace",
                          fontSize: 12,
                        }}
                      >
                        {permission.id}
                      </TableCell>
                      <TableCell align="right" sx={{ pr: 0 }}>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => setPendingDelete(permission)}
                            sx={{
                              color: colorTextMuted,
                              "&:hover": {
                                color: colorActionDanger,
                                backgroundColor: colorSurfaceDangerHover,
                              },
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </InfoCard>
        )}
      </Box>

      {/* Create dialog */}
      <Dialog open={createOpen} onClose={handleCloseCreate} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>New Permission</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              autoFocus
              label="Name"
              fullWidth
              size="small"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              required
              helperText="Must be unique (e.g. staff_read, organization_update)"
            />
            <TextField
              label="Description"
              fullWidth
              size="small"
              value={createDescription}
              onChange={(e) => setCreateDescription(e.target.value)}
              placeholder="Optional"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <CustomButton color="inverted" onClick={handleCloseCreate}>
            Cancel
          </CustomButton>
          <CustomLoadingButton
            loading={createMutation.isPending}
            disabled={!createName.trim()}
            onSubmit={handleCreate}
            label="Create"
            color="success"
            fullwidth={false}
          />
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={!!pendingDelete}
        onClose={() => !deleteMutation.isPending && setPendingDelete(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Delete Permission</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the permission{" "}
            <strong>{pendingDelete?.name}</strong>? Any roles currently using it
            will lose this permission.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <CustomButton
            color="inverted"
            onClick={() => setPendingDelete(null)}
            disabled={deleteMutation.isPending}
          >
            Cancel
          </CustomButton>
          <CustomButton
            disabled={deleteMutation.isPending}
            onClick={handleDelete}
            startIcon={
              deleteMutation.isPending ? (
                <CircularProgress size={14} color="inherit" />
              ) : undefined
            }
            sx={{
              backgroundColor: colorActionDanger,
              color: "#fff",
              "&:hover": { backgroundColor: colorTextError },
            }}
          >
            Delete
          </CustomButton>
        </DialogActions>
      </Dialog>

      <SnackBar
        open={snack.open}
        message={snack.message}
        severity={snack.severity}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      />
    </>
  );
};

export default Permissions;
